import React, { createContext, useContext, useState } from 'react';

interface OrderLimitsConfig {
  maxArticlesPerOrder?: number; // null = unlimited
  requireExactCount?: boolean; // true = must be exact, false = up to max
}

interface OrderLimitsContextType {
  config: OrderLimitsConfig;
  setConfig: (config: OrderLimitsConfig) => void;
  isOrderValid: (articleCount: number) => boolean;
  getOrderStatus: (articleCount: number) => {
    isValid: boolean;
    isComplete: boolean;
    message?: string;
    color: 'success' | 'warning' | 'danger' | 'primary';
  };
}

const OrderLimitsContext = createContext<OrderLimitsContextType | undefined>(undefined);

export function OrderLimitsProvider({ children }: { children: React.ReactNode }) {
  // Default config - will be loaded from backend later
  const [config, setConfig] = useState<OrderLimitsConfig>({
    maxArticlesPerOrder: undefined, // No limit for now, will come from portal settings
    requireExactCount: false        // No exact count requirement
  });

  const isOrderValid = (articleCount: number): boolean => {
    if (!config.maxArticlesPerOrder) return true; // Unlimited
    
    if (config.requireExactCount) {
      return articleCount === config.maxArticlesPerOrder;
    } else {
      return articleCount <= config.maxArticlesPerOrder;
    }
  };

  const getOrderStatus = (articleCount: number) => {
    if (!config.maxArticlesPerOrder) {
      return {
        isValid: true,
        isComplete: true,
        color: 'success' as const
      };
    }

    const maxCount = config.maxArticlesPerOrder;
    
    if (config.requireExactCount) {
      if (articleCount === maxCount) {
        return {
          isValid: true,
          isComplete: true,
          message: `Order complete (${articleCount}/${maxCount})`,
          color: 'success' as const
        };
      } else if (articleCount < maxCount) {
        const needed = maxCount - articleCount;
        return {
          isValid: false,
          isComplete: false,
          message: `Add ${needed} more article${needed > 1 ? 's' : ''} to complete order (${articleCount}/${maxCount})`,
          color: 'warning' as const
        };
      } else {
        const excess = articleCount - maxCount;
        return {
          isValid: false,
          isComplete: false,
          message: `Remove ${excess} article${excess > 1 ? 's' : ''} - maximum ${maxCount} per order (${articleCount}/${maxCount})`,
          color: 'danger' as const
        };
      }
    } else {
      // Max limit mode (not exact)
      if (articleCount <= maxCount) {
        return {
          isValid: true,
          isComplete: true,
          color: articleCount === maxCount ? 'success' as const : 'primary' as const
        };
      } else {
        const excess = articleCount - maxCount;
        return {
          isValid: false,
          isComplete: false,
          message: `Remove ${excess} article${excess > 1 ? 's' : ''} - maximum ${maxCount} per order (${articleCount}/${maxCount})`,
          color: 'danger' as const
        };
      }
    }
  };

  return (
    <OrderLimitsContext.Provider value={{
      config,
      setConfig,
      isOrderValid,
      getOrderStatus
    }}>
      {children}
    </OrderLimitsContext.Provider>
  );
}

export function useOrderLimits() {
  const context = useContext(OrderLimitsContext);
  if (context === undefined) {
    throw new Error('useOrderLimits must be used within a OrderLimitsProvider');
  }
  return context;
}